import { LiveDataRow } from "../models/LiveDataRow";
import { formatChecklistValue, formatSerialValue } from "./checklistValues";
import { haystack } from "./diagnosticMatchers";

/** One add-on device and what was reported about it. */
export interface IoxDevice {
  label: string;
  value: string;
  /** Nothing about this device arrived in the live window; everything shown is from the history. */
  fromFallback: boolean;
}

export interface IoxFieldLabels {
  type: string;
  serial: string;
  status: string;
}

/**
 * The field names a reading can carry, in both languages a Geotab database may use them in.
 * A device reports one reading per field — "IOX-CAN Serial", "IOX-CAN Type" — so the field word
 * has to come off the name to find the device it belongs to.
 */
const FIELD_WORD =
  "serial(?:\\s*number)?|s\\/n|seriennummer|status|state|zustand|type|typ|number|nummer";

const TRAILING_FIELD = new RegExp(`[\\s_-]*(?:${FIELD_WORD})\\s*$`, "i");
const ONLY_FIELD = new RegExp(`^(?:${FIELD_WORD})$`, "i");
const FIELD_AND_REST = new RegExp(`[\\s_-]*(?:${FIELD_WORD})[\\s_-]*.*$`, "i");

const IS_TYPE = /\b(?:type|typ)\b/i;
const IS_SERIAL = /\b(?:serial|seriennummer|s\/n)\b/i;
const IS_STATUS = /\b(?:status|state|zustand)\b/i;

/**
 * Whether a reading describes an add-on device.
 *
 * The name or the id saying "IOX" is the reliable signal. Some databases instead name the
 * reading after the field alone ("Serial") and put the hardware in the id -- but only then is
 * the id worth reading that way, because plenty of ordinary readings carry the word "device" in
 * their id, starting with the telematics unit's own voltage.
 */
export function isIoxRow(row: LiveDataRow): boolean {
  // Tested before the name is normalised: collapsing whitespace can neither create nor destroy
  // the substring, and almost no reading on a vehicle is about an add-on device.
  if (/iox/i.test(haystack(row))) {
    return true;
  }
  return (
    ONLY_FIELD.test(normalizeName(row.diagnosticName)) &&
    /device|telematics/i.test(row.diagnosticId ?? "")
  );
}

/** The reading name with its whitespace collapsed, as the field patterns expect it. */
function normalizeName(diagnosticName: string): string {
  return diagnosticName.replace(/\s+/g, " ").trim();
}

interface DeviceFields {
  type?: string;
  serial?: string;
  status?: string;
}

interface DeviceEntry {
  fields: DeviceFields;
  /** At least one of the fields below came from the live window. */
  reportingNow: boolean;
}

/**
 * Gathers what the vehicle reported about its add-on devices.
 *
 * Geotab reports each field of an add-on device as its own reading, so "IOX-CAN Serial" and
 * "IOX-CAN Type" describe one device and have to end up as one line. The device is identified by
 * the reading's name with the field word removed; where the name is only a field word, the
 * diagnostic id is used instead, because some databases put the device there.
 *
 * A device counts as reporting now as soon as any one of its fields arrived in the live window.
 * Requiring all of them would mark almost every device as silent: a serial number is sent once
 * and then rarely again, while a status arrives constantly.
 *
 * @param rows - Readings from the live window.
 * @param fallbackRows - Readings from the wider history, used for whatever the live window misses.
 * @param language - The language the app is running in.
 * @param labels - Translated field names for the summary line.
 */
export function buildIoxDevices(
  rows: LiveDataRow[],
  fallbackRows: LiveDataRow[],
  language: string,
  labels: IoxFieldLabels,
): IoxDevice[] {
  const byDevice = new Map<string, DeviceEntry>();

  // The live window first, so its values win and the history only fills what is still missing.
  collectFields(byDevice, rows, language, true);
  collectFields(byDevice, fallbackRows, language, false);

  return [...byDevice.entries()]
    .map(([label, entry]) => ({
      label,
      value: describe(entry.fields, labels),
      fromFallback: !entry.reportingNow,
    }))
    .filter(device => device.value !== "")
    .sort((left, right) => left.label.localeCompare(right.label));
}

function collectFields(
  byDevice: Map<string, DeviceEntry>,
  rows: LiveDataRow[],
  language: string,
  isLive: boolean,
): void {
  for (const row of rows) {
    if (!isIoxRow(row)) {
      continue;
    }

    const name = normalizeName(row.diagnosticName);
    const key = deviceKey(name, row.diagnosticId);
    const entry = byDevice.get(key) ?? { fields: {}, reportingNow: false };
    const { fields } = entry;
    let filled = false;

    if (IS_TYPE.test(name) && !IS_SERIAL.test(name) && !IS_STATUS.test(name)) {
      filled ||= setField(fields, "type", formatChecklistValue(row, language));
    }
    if (IS_SERIAL.test(name)) {
      filled ||= setField(fields, "serial", formatSerialValue(row));
    }
    if (IS_STATUS.test(name)) {
      filled ||= setField(fields, "status", formatChecklistValue(row, language));
    }

    entry.reportingNow ||= isLive && filled;
    byDevice.set(key, entry);
  }
}

/** Writes a field unless it already has a value. Reports whether anything usable was written. */
function setField(fields: DeviceFields, name: keyof DeviceFields, value: string): boolean {
  if (fields[name] !== undefined || value === "") {
    return false;
  }
  fields[name] = value;
  return true;
}

function deviceKey(name: string, diagnosticId: string): string {
  if (!ONLY_FIELD.test(name)) {
    const withoutField = name.replace(TRAILING_FIELD, "").trim();
    if (withoutField) {
      return withoutField;
    }
  }

  return keyFromDiagnosticId(diagnosticId) || name || diagnosticId;
}

/** Strips the field word from an id such as `aIoxCanSerial`, leaving the device part. */
function keyFromDiagnosticId(diagnosticId: string): string {
  return String(diagnosticId ?? "")
    .replace(FIELD_AND_REST, "")
    .replace(/[\s_-]+$/, "")
    .trim();
}

function describe(fields: DeviceFields, labels: IoxFieldLabels): string {
  return [
    fields.type ? `${labels.type}: ${fields.type}` : "",
    fields.serial ? `${labels.serial}: ${fields.serial}` : "",
    fields.status ? `${labels.status}: ${fields.status}` : "",
  ]
    .filter(Boolean)
    .join(", ");
}
